import React, { useMemo } from 'react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { useThemeContext } from '../theme/ThemeProvider';
import { buildMuiTheme } from '../theme/muiBridge';

type Props = {
  children: React.ReactNode;
  className?: string;
  /**
   * If true, the surface fills the viewport height. Use for full-page screens.
   * Default false — let the parent layout control sizing.
   */
  fillViewport?: boolean;
};

/**
 * Opt-in wrapper that switches MUI's theme to the design-system tokens
 * inside this subtree, applies the .ds-root reset, and gives the
 * background the new bg color.
 *
 * Use this to migrate one screen at a time without flipping the rest
 * of the app.
 */
export const DesignSystemSurface: React.FC<Props> = ({
  children,
  className,
  fillViewport = false,
}) => {
  const { tokens } = useThemeContext();
  const muiTheme = useMemo(() => buildMuiTheme(tokens), [tokens]);

  const style: React.CSSProperties = {
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
    ...(fillViewport ? { minHeight: '100vh' } : {}),
  };

  return (
    <MuiThemeProvider theme={muiTheme}>
      <div
        className={`ds-root${className ? ` ${className}` : ''}`}
        style={style}
      >
        {children}
      </div>
    </MuiThemeProvider>
  );
};
